import { Image as ImageIcon } from 'iconsax-react';
import type { ChangeEvent, ReactNode } from 'react';
import { type Control, type FieldPath, type FieldValues, useController } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import usePickMediaFile from '@/hooks/files/use-pick-media-file';
import usePreviewImage from '@/hooks/files/use-preview-image';
import useSystemSetting from '@/hooks/files/use-system-setting';
import useUserPlatform, { useUserHints } from '@/hooks/use-user-platform';
import type { Uploadable } from '@/models/uploadable';
import { addToast } from '@/stores/toast';
import { readableFileSize } from '@/utils/file-size';
import { FileType } from '@/utils/file-type';
import cn from '@/utils/merge-class-names';
import { convertFileToUploadable } from '@/utils/uploadable';

interface Props<TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>> {
  disabled?: boolean;
  name: TName;
  control: Control<TFieldValues>;
  className?: string;
  fallback?: ReactNode;
  children: (fieldValue: Uploadable | undefined, preview: string | undefined) => ReactNode;
  limitSize?: number;
}

export function UploadImageField<TF extends FieldValues>({
  disabled,
  name,
  control,
  children,
  fallback,
  className,
}: Props<TF>) {
  const { field } = useController({ control, name, disabled });
  const [preview, setPreview] = usePreviewImage();
  const { ref, promptPickMedia, clearMedia, pickMedia } = usePickMediaFile();
  const { isDesktop } = useUserPlatform();
  const { hints } = useUserHints();
  const { t } = useTranslation();
  const { getFileSizeLimit } = useSystemSetting();
  const fileSizeLimit = getFileSizeLimit('image');

  const handleSelectImage = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];

    if (file) {
      if (fileSizeLimit && file.size > fileSizeLimit) {
        clearMedia();

        addToast({
          type: 'error',
          message: t('errors.file-size-limit', { limit: readableFileSize(fileSizeLimit) }),
        });
      } else {
        setPreview(file);
        field.onChange(convertFileToUploadable(file));
      }
    }
  };
  const handleButtonClick = () => {
    if (hints.osType === 'desktop' || isDesktop) {
      pickMedia({ type: 'album', accept: FileType.Image });
    } else {
      promptPickMedia();
    }
  };
  return (
    <>
      {field.value ? (
        <button
          type='button'
          disabled={field.disabled}
          className={cn('btn btn-circle btn-ghost', className)}
          onClick={handleButtonClick}>
          {children(field.value, preview)}
        </button>
      ) : (
        <button
          type='button'
          className={cn('btn btn-circle btn-ghost size-14 bg-base-200', className)}
          onClick={handleButtonClick}>
          {fallback || <ImageIcon className='size-6' />}
        </button>
      )}

      <input
        type='file'
        className='sr-only'
        accept='image/*'
        {...field}
        ref={(r) => {
          field.ref(r);
          ref.current = r;
        }}
        value=''
        onChange={handleSelectImage}
      />
    </>
  );
}
